import React, { useState } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'

import { useEnvironmentUpdates } from '../../../hooks/useEnvironmentUpdates'
import DiffViewer, { DiffMethod } from 'react-diff-viewer'
import Network from '../../general/Network'
import { parseName } from '../../../utils/math-utils'
import moment from 'moment'
import ConnectWallet from '../ConnectWallet'
import TransactionButton from '../TransactionButton'
import { useSafeConfig } from 'src/context/SafeConfigProvider'
import AddPermissionsTimelock from './components/AddPermissionsGeneric'
import { useAccount } from 'wagmi'
import { safeTransactionUrl } from 'src/utils/web3-utils'
import open from '../../../assets/link.png'

const Submit: React.FC<{ chainId: number; environmentAuthorizer: string }> = ({
  chainId,
  environmentAuthorizer,
}) => {
  const params = useParams()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(5)
  const [submitted, setSubmitted] = useState(false)
  const [completed, setCompleted] = useState(false)

  const updates = useEnvironmentUpdates({
    mimicId: params.id,
    chainId: String(chainId),
    submitted,
    completed,
    limit,
    page,
  })
  const [status, setStatus] = useState<string | null>(null)
  const { safeAddress } = useSafeConfig()
  const { chain } = useAccount()
  const sameNetwork = chainId.toString() === chain?.id.toString()

  // The /environments/updates endpoint already filters by mimicId and chainId, and
  // paginates; filtering again here would punch holes in the returned pages.
  const showList = updates?.data?.environments ?? []
  const totalPages = updates?.data?.pages ?? 1

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage)
  }

  return (
    <Section>


      <SectionHeader>
        <SectionTitle>Ready to submit</SectionTitle>
        <ConnectWallet />
      </SectionHeader>

      <MetaRow>
        <SafeLink href={safeTransactionUrl(chainId, safeAddress)} target="_blank" rel="noreferrer">
          <span>Safe:</span>
          <code>{safeAddress}</code>
          <img src={open} alt="open" />
        </SafeLink>
        {!sameNetwork && (
          <NetworkWarning>⚠ Network mismatch — switch to the selected chain</NetworkWarning>
        )}
      </MetaRow>

      <FilterRow>
        <CheckLabel>
          <input
            type="checkbox"
            checked={submitted}
            onChange={(e) => {
              setSubmitted(e.target.checked)
              setPage(1)
            }}
          />
          Submitted
        </CheckLabel>
        <CheckLabel>
          <input
            type="checkbox"
            checked={completed}
            onChange={(e) => {
              setCompleted(e.target.checked)
              setPage(1)
            }}
          />
          Completed
        </CheckLabel>
        <SelectLabel>
          Per page
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value))
              setPage(1)
            }}
          >
            <option value={1}>1</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
          </select>
        </SelectLabel>
      </FilterRow>

      {status && <StatusMsg>{status}</StatusMsg>}

      {showList.length > 0 ? (
        showList.map((item: any, index: number) => (
          <UpdateCard
            key={index}
            item={item}
            setStatus={setStatus}
            chainId={chainId}
            environmentAuthorizer={environmentAuthorizer}
            safeAddress={safeAddress}
          />
        ))
      ) : (
        <EmptyState>No pending updates for this network</EmptyState>
      )}

      <Pagination>
        <PageBtn type="button" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
          Previous
        </PageBtn>
        <PageCount>
          Page
          <PageInput
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => {
              const val = Number(e.target.value)
              if (val >= 1 && val <= totalPages) setPage(val)
            }}
          />
          of {totalPages}
        </PageCount>
        <PageBtn type="button" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>
          Next
        </PageBtn>
      </Pagination>
    </Section>
  )
}

const UpdateCard: React.FC<{ item: any; environmentAuthorizer: any }> = ({
  setStatus,
  item,
  chainId,
  environmentAuthorizer,
  safeAddress,
}) => {
  const [startChanges, setStartChanges] = useState<any>(null)
  const [endChanges, setEndChanges] = useState<any>(null)

  const metadata = `${new Date(item.createdAt).toLocaleString()} · ${moment(item.createdAt).fromNow()} · by ${item.createdBy} · ${item?.encodedChanges.length} encoded changes`

  let oldValue = ''
  let newValue = ''
  try {
    oldValue = JSON.stringify(item.currentState, null, 2)
    newValue = JSON.stringify(item.expectedState, null, 2)
  } catch {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardLeft>
          <CardTitle>
            <span>{parseName(item.currentState.namespace)}</span>
            <Network network={item.currentState.network} small={true} />
            {item.safeTxHash && <ExecutedBadge>executed</ExecutedBadge>}
          </CardTitle>
          <CardMeta>{metadata}</CardMeta>
        </CardLeft>
        <CardRight>
          <TransactionButton
            setStatus={setStatus}
            startChanges={startChanges}
            endChanges={endChanges}
            encodedChanges={item.encodedChanges}
            safeAddress={safeAddress}
            chainId={chainId}
            namespace={parseName(item.currentState.namespace)}
            metadata={metadata}
            mimicId={item.mimicId}
            itemId={item._id}
          />
        </CardRight>
      </CardHeader>

      <AddPermissionsTimelock
        chainId={chainId}
        setStartChanges={setStartChanges}
        setEndChanges={setEndChanges}
        encodedChanges={item.encodedChanges}
        environmentAuthorizer={environmentAuthorizer}
      />

      <DiffWrap>
        <DiffViewer
          oldValue={oldValue}
          newValue={newValue}
          splitView={true}
          compareMethod={DiffMethod.WORDS}
          hideLineNumbers={true}
          showDiffOnly={true}
          leftTitle="Current"
          rightTitle="Proposed"
          useDarkTheme={true}
          styles={{
            variables: {
              dark: {
                diffViewerBackground: '#0f0f0f',
                diffViewerTitleBackground: '#111',
                diffViewerTitleColor: '#a0a0a0',
                diffViewerTitleBorderColor: 'rgba(255,255,255,0.07)',
                addedBackground: 'rgba(51,194,176,0.08)',
                addedColor: '#ededed',
                removedBackground: 'rgba(255,68,68,0.08)',
                removedColor: '#ededed',
                wordAddedBackground: 'rgba(51,194,176,0.25)',
                wordRemovedBackground: 'rgba(255,68,68,0.25)',
                addedGutterBackground: 'rgba(51,194,176,0.15)',
                removedGutterBackground: 'rgba(255,68,68,0.15)',
                gutterBackground: '#0d0d0d',
                gutterBackgroundDark: '#0a0a0a',
                highlightBackground: 'rgba(111,92,230,0.08)',
                highlightGutterBackground: 'rgba(111,92,230,0.12)',
                codeFoldBackground: '#111',
                emptyLineBackground: '#0d0d0d',
                codeFoldContentColor: '#444',
              },
            },
          }}
        />
      </DiffWrap>
    </Card>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Section = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const SectionTitle = styled.h2`
  color: #ededed;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
`

const SafeLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #777;
  text-decoration: none;
  font-size: 12px;
  transition: color 0.15s;

  span { color: #777; }

  code {
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 11px;
    color: #a1a1a1;
  }

  img {
    height: 11px;
    width: 11px;
    opacity: 0.4;
  }

  &:hover { color: #a0a0a0; code { color: #a0a0a0; } img { opacity: 0.7; } }
`

const NetworkWarning = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: rgba(255, 204, 51, 0.08);
  border: 1px solid rgba(255, 204, 51, 0.2);
  border-radius: 5px;
  color: #FFCC33;
  font-size: 12px;
`

const StatusMsg = styled.div`
  padding: 8px 12px;
  background: rgba(111, 92, 230, 0.08);
  border: 1px solid rgba(111, 92, 230, 0.2);
  border-radius: 6px;
  color: #a89aff;
  font-size: 12px;
  margin-bottom: 16px;
`

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #777;
  font-size: 13px;
  background: #111;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
`

const Card = styled.div`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

const CardLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ededed;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.02em;
`

const CardMeta = styled.div`
  color: #a1a1a1;
  font-size: 11px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  line-height: 1.5;
`

const CardRight = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
`

const ExecutedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  background: rgba(51, 194, 176, 0.1);
  border: 1px solid rgba(51, 194, 176, 0.2);
  border-radius: 4px;
  color: #33C2B0;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

const DiffWrap = styled.div`
  font-size: 12px;

  /* Dark theme overrides */
  pre { font-family: 'SF Mono', 'Fira Code', monospace !important; }
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`

const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #a0a0a0;
  font-size: 12px;
  cursor: pointer;

  input[type='checkbox'] {
    accent-color: #6f5ce6;
    cursor: pointer;
    margin: 0;
  }
`

const SelectLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #777;
  font-size: 12px;

  select {
    height: 26px;
    background: #0d0d0d;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 4px;
    color: #a0a0a0;
    font-size: 12px;
    padding: 0 6px;
    outline: none;
    cursor: pointer;

    &:focus { border-color: rgba(111, 92, 230, 0.5); }
  }
`

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
`

const PageBtn = styled.button`
  height: 26px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  color: #a0a0a0;
  font-size: 11px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover:not(:disabled) { border-color: rgba(111, 92, 230, 0.4); color: #a89aff; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`

const PageCount = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #777;
  font-size: 12px;
`

const PageInput = styled.input`
  width: 48px;
  height: 26px;
  background: #0d0d0d;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  color: #ededed;
  font-size: 12px;
  text-align: center;
  outline: none;

  &:focus { border-color: rgba(111, 92, 230, 0.5); }
`

export const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

export default Submit
